import React, { FC, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { Route, Routes, Navigate } from 'react-router-dom';
import PrivateOutlet from './components/auth/PrivateOutlet';
import BasePage from './pages/base/BasePage';
import themePreset from './libs/mui/theme';
import { enGB, bg } from 'date-fns/locale';
import setDefaultOptions from 'date-fns/setDefaultOptions';
import { useTranslation } from 'react-i18next';
import { TaskScheduler2 } from './pages/taskScheduler2/TaskScheduler2.page';
import {
  DataGridLocale,
  DatePickerLocale,
  ModalProvider,
  MuiCoreLocale,
  TanstackTableLocaleProvider,
  useToast,
} from '@thingslog/ui-components';
import { useTableLocale } from './libs/i18n/useTableLocale';
import ScheduledTasksHistory from './pages/taskSchedulerHistory/TaskSchedulerHistory.page';
import CommandTaskOverview from './pages/taskSchedulerHistory/CommandTaskOverview.page';
import LicenseError from './pages/licenseError/LicenseError';
import Axios from './libs/axios/Axios';
import { SessionAuthProvider, SessionAuthSetup, useAuth } from '@thingslog/utils';
import JwtValidator from './utils/JwtValidator';
import { clearSessionUser, getSessionUser, setSessionUser } from './utils/sessionUser';

const Home2 = React.lazy(() => import('./pages/home2/Home2.page'));
const Dashboard2 = React.lazy(() => import('./pages/dashboard2/Dashboard.page'));
const AlarmsExport = React.lazy(() => import('./pages/alarms/AlarmsExport'));
const Trashbags = React.lazy(() => import('./pages/trashbags/Trashbags'));
const Collections = React.lazy(() => import('./pages/collections/Collections'));
const TruckCollections = React.lazy(() => import('./pages/truckCollections/TruckCollections'));
const TruckCollectionsByContainer = React.lazy(
  () => import('./pages/truckCollectionsByContainer/TruckCollectionsByContainer')
);
const ContainerCollections = React.lazy(
  () => import('./pages/containerCollections/ContainerCollections')
);
import NavbarLayoutRoute from './components/navigation/NavbarLayoutRoute';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { AutoAuth, ErrorResponse, Jwt } from '@thingslog/repositories';
import { userQueryClient } from './utils/ReactQueryClients';
import { DateSelectionProvider } from './store';
import { getSelectedCompanyId } from './hooks';

interface Props {}

const AppContent: FC = () => {
  const { user } = useAuth();
  const { decodedToken } = useMemo(() => new JwtValidator(user), [user]);
  const { useUser } = useMemo(() => userQueryClient, []);
  const { i18n } = useTranslation();
  const { toast } = useToast();
  const tableLocale = useTableLocale();

  const theme = useMemo(() => {
    return createTheme(
      themePreset,
      DataGridLocale[i18n.language],
      MuiCoreLocale[i18n.language],
      DatePickerLocale[i18n.language]
    );
  }, [i18n.language]);

  const currentUser = useUser(decodedToken?.email!, {
    enabled: decodedToken?.email !== undefined,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const currUserLang = currentUser.data?.language ?? null;
    const savedLanguage = localStorage.getItem('i18nextLng') ?? null;
    if (currUserLang) {
      const languageToUse = savedLanguage ?? currUserLang;
      if (i18n.language !== languageToUse) {
        i18n.changeLanguage(languageToUse);
      }
      const locale = languageToUse === 'bg' ? bg : enGB;
      setDefaultOptions({ locale: locale });
    }
  }, [currentUser]);

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <ModalProvider>
          <CssBaseline />
          <SessionAuthSetup
            Axios={Axios}
            onRequest={(request: AxiosRequestConfig): AxiosRequestConfig => {
              const jwtValidator = new JwtValidator();
              const isPublic =
                window.runConfig?.autoAuth?.some(
                  (a: AutoAuth) => a.url === window.location.hostname && a.isPublic
                ) ?? false;

              if (jwtValidator.hasCompanySelectionRole()) {
                const selectedCompanyId = getSelectedCompanyId();

                if (selectedCompanyId !== undefined) {
                  request.headers = request.headers ?? {};
                  request.headers['X-Company-Id'] = String(selectedCompanyId);
                }
              }

              if (
                (isPublic || jwtValidator.hasRole('ROLE_PUBLIC')) &&
                !request?.url?.includes('weatherapi.com') &&
                !request?.url?.includes('alarms')
              ) {
                request.url = request.url?.replace('/api', '/public-api');
              }
              return request;
            }}
            onErrorCode={(statusCode: number, error: AxiosError<ErrorResponse>): void => {
              switch (statusCode) {
                case 401:
                  break;
                case 403:
                  if (error.response?.data.errorCode === 'NO_VALID_OR_ACTIVE_LICENSE') {
                    if (window.location.pathname !== '/portal/restricted-access') {
                      window.location.href = `${window.location.origin}/portal/restricted-access`;
                    }
                  } else {
                    if (
                      window.location.pathname !== '/portal?error=forbidden' &&
                      window.location.pathname !== '/app/signin'
                    ) {
                      toast({
                        type: 'error',
                        message: i18n.t('error_forbidden_operation_not_allowed'),
                      });
                    }
                  }
                  break;
                default:
                  break;
              }
            }}
          />
          <DateSelectionProvider>
            <TanstackTableLocaleProvider locale={tableLocale}>
              <Routes>
                <Route element={<NavbarLayoutRoute />}>
                  <Route index element={<BasePage />} />
                  <Route path="/home" element={<PrivateOutlet isInternal={false} />}>
                    <Route path="" element={<Home2 />} />
                  </Route>
                  <Route path="/dashboard" element={<PrivateOutlet isInternal={false} />}>
                    <Route path=":urlDeviceNumber" element={<Dashboard2 />} />
                  </Route>
                  <Route path="/alarms" element={<PrivateOutlet isInternal={true} />}>
                    <Route index element={<AlarmsExport />} />
                  </Route>
                  <Route path="/taskScheduler" element={<PrivateOutlet isInternal={true} />}>
                    <Route path="" element={<TaskScheduler2 />} />
                    <Route path="edit/:scheduledEventId" element={<TaskScheduler2 />} />
                    <Route path="history/:scheduledEventId" element={<ScheduledTasksHistory />} />
                    <Route
                      path="history/:scheduledEventId/:scheduledEventLogId"
                      element={<CommandTaskOverview />}
                    />
                  </Route>
                  <Route path="/wastebags" element={<PrivateOutlet isInternal={true} />}>
                    <Route path="" element={<Trashbags />} />
                  </Route>
                  <Route path="/collections" element={<PrivateOutlet isInternal={true} />}>
                    <Route path="" element={<Collections />} />
                  </Route>
                  <Route path="/truck-collections" element={<PrivateOutlet isInternal={true} />}>
                    <Route path="" element={<TruckCollections />} />
                  </Route>
                  <Route
                    path="/truck-collections-by-container"
                    element={<PrivateOutlet isInternal={true} />}
                  >
                    <Route path="" element={<TruckCollectionsByContainer />} />
                  </Route>
                  <Route
                    path="/container-collections"
                    element={<PrivateOutlet isInternal={true} />}
                  >
                    <Route path="" element={<ContainerCollections />} />
                  </Route>
                </Route>
                <Route path="/restricted-access" element={<PrivateOutlet isInternal={false} />}>
                  <Route path="" element={<LicenseError />} />
                </Route>
                <Route path="*" element={<Navigate replace to="/" />} />
              </Routes>
              <div id="confirmation-dialog"></div> {/* DO NOT DELETE */}
            </TanstackTableLocaleProvider>
          </DateSelectionProvider>
        </ModalProvider>
      </ThemeProvider>
    </React.Fragment>
  );
};

const App: FC<Props> = (props: Props) => {
  return (
    <SessionAuthProvider
      Axios={Axios}
      onSuccessfulAuthentication={(user: Jwt): void => {
        const previousUser = getSessionUser();
        const jwtValidator = new JwtValidator(user);

        if (
          !previousUser?.email ||
          previousUser.email !== user.email ||
          !jwtValidator.hasCompanySelectionRole()
        ) {
          localStorage.removeItem('selectedCompany');
        }

        setSessionUser(user);
      }}
      onFailedAuthentication={(): void => {
        clearSessionUser();
        localStorage.removeItem('selectedCompany');
      }}
    >
      <AppContent />
    </SessionAuthProvider>
  );
};

export default App;
